import { FC } from 'react';
import { FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, Box } from '@mui/material';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useAcademicsFiltersContext } from '../contexts/academicsFilters.context';
import { useGuardianState, useGuardianActions } from '../../guardian/contexts/guardian.context';

const GRADE_OPTIONS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

const getAcademicYearOptions = (): string[] => {
  const current = new Date().getFullYear();
  const options: string[] = [];
  for (let i = 0; i < 5; i++) {
    const y = current - i;
    options.push(`${y}-${y + 1}`);
  }
  return options;
};

const GuardianChildSelector: FC = () => {
  const { translateText } = useTranslation();
  const { selectedChildId, children } = useGuardianState();
  const { setSelectedChild } = useGuardianActions();
  if (children.length <= 1) return null;
  return (
    <FormControl size="small" sx={{ minWidth: 150 }} data-testid="academics-filter-child">
      <InputLabel>{translateText('guardian.academics.filters.child')}</InputLabel>
      <Select
        value={selectedChildId || ''}
        onChange={(e) => setSelectedChild(e.target.value || null)}
        label={translateText('guardian.academics.filters.child')}
      >
        {children.map((child) => (
          <MenuItem key={child.id} value={child.id}>
            {child.name} {child.grade ? `(${child.grade})` : ''}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

interface AcademicsFilterBarProps {
  isGuardian?: boolean;
}

const AcademicsFilterBar: FC<AcademicsFilterBarProps> = ({ isGuardian = false }) => {
  const { translateText } = useTranslation();
  const { academicYear, grade, setAcademicYear, setGrade } = useAcademicsFiltersContext();

  const academicYearOptions = getAcademicYearOptions();

  const handleAcademicYearChange = (e: SelectChangeEvent<string>) => {
    setAcademicYear(e.target.value);
  };

  const handleGradeChange = (e: SelectChangeEvent<string>) => {
    setGrade(e.target.value);
  };

  return (
    <Box
      sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}
      data-testid="academics-filter-bar"
    >
      {isGuardian && <GuardianChildSelector />}
      <FormControl size="small" sx={{ minWidth: 140 }} data-testid="academics-filter-year">
        <InputLabel>{translateText('academics.filters.academicYear')}</InputLabel>
        <Select
          value={academicYear}
          onChange={handleAcademicYearChange}
          label={translateText('academics.filters.academicYear')}
        >
          {academicYearOptions.map((y) => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 100 }} data-testid="academics-filter-grade">
        <InputLabel>{translateText('academics.filters.grade')}</InputLabel>
        <Select
          value={grade}
          onChange={handleGradeChange}
          label={translateText('academics.filters.grade')}
        >
          <MenuItem value="">{translateText('common.all')}</MenuItem>
          {GRADE_OPTIONS.map((g) => (
            <MenuItem key={g} value={g}>
              {g}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default AcademicsFilterBar;
